import React, { Suspense, lazy } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Task, FullProject, ProjectMetadata } from '../../types';
import { useProject } from '../../contexts/ProjectContext';
import { useUI } from '../../contexts/UIContext';
import { useAuth } from '../../contexts/AuthContext';
import { canUserEditTask } from '../../utils/projectPermissions';
import { ErrorBoundary } from '../ErrorBoundary';
import { ProjectManagementModalsGroup } from './ProjectManagementModalsGroup';
import { SystemAndOnboardingModalsGroup } from './SystemAndOnboardingModalsGroup';

const TaskEditCard = lazy(() => import('../TaskEditCard'));
const GanttLegend = lazy(() => import('../GanttLegend'));
const ProjectChatPanel = lazy(() => import('../ProjectChatPanel'));
const AIProjectPlanner = lazy(() => import('../AIProjectPlanner'));
const ProjectBiographyModal = lazy(() => import('../ProjectBiographyModal'));
const TimeMachineModal = lazy(() => import('../TimeMachineModal'));
const ExportProModal = lazy(() =>
  import('../ExportProModal').then((m) => ({ default: m.ExportProModal }))
);

export interface GlobalModalsContainerProps {
  modalRegistry?: any;
  showBiographyModal?: boolean;
  setShowBiographyModal?: (open: boolean) => void;
  isTimeMachineOpen: boolean;
  setIsTimeMachineOpen: (open: boolean) => void;
  showCreditsModal?: boolean;
  setShowCreditsModal?: (open: boolean) => void;
  showAllowedUsersModal?: boolean;
  setShowAllowedUsersModal?: (open: boolean) => void;
  showPrivacyModal?: boolean;
  setShowPrivacyModal?: (open: boolean) => void;
  showAccountDeletionModal?: boolean;
  setShowAccountDeletionModal?: (open: boolean) => void;
  showExportProModal?: boolean;
  setShowExportProModal?: (open: boolean) => void;
  showCommandPalette?: boolean;
  setShowCommandPalette?: (open: boolean) => void;
  showShortcutsHelpModal?: boolean;
  setShowShortcutsHelpModal?: (open: boolean) => void;
  showUserProfileModal?: boolean;
  setShowUserProfileModal?: (open: boolean) => void;
  showCreditsAndHelpModal?: boolean;
  setShowCreditsAndHelpModal?: (open: boolean) => void;
  showQuickAddTaskModal?: boolean;
  setShowQuickAddTaskModal?: (open: boolean) => void;
  showOnboardingWizard?: boolean;
  setShowOnboardingWizard?: (open: boolean) => void;
  showGuidedTour?: boolean;
  setShowGuidedTour?: (open: boolean) => void;
  showDragDropHint?: boolean;
  setShowDragDropHint?: (open: boolean) => void;

  isProjectEditor: boolean;
  categories: any[];
  isRightSidebarOpen: boolean;
  setIsRightSidebarOpen: (open: boolean) => void;
  handleAddTask: (type?: any, parentId?: string | number, projectId?: string, initialName?: string) => any;

  editingTask?: Task | null;
  setEditingTask?: (task: Task | null) => void;
  projectToDuplicate?: string | null;
  setProjectToDuplicate?: (id: string | null) => void;
  projectToDelete?: string | null;
  setProjectToDelete?: (id: string | null) => void;
  projectToRename?: string | null;
  setProjectToRename?: (id: string | null) => void;
  projectToShare?: string | null;
  setProjectToShare?: (id: string | null) => void;
  showProjectOverviewModal?: boolean;
  setShowProjectOverviewModal?: (open: boolean) => void;
  showAIPlanner?: boolean;
  setShowAIPlanner?: (open: boolean) => void;
  showLegend?: boolean;
  setShowLegend?: (open: boolean) => void;
  showChatPanel?: boolean;
  setShowChatPanel?: (open: boolean) => void;
  projects?: FullProject[];
  allProjects?: FullProject[];
  activeProject?: FullProject | null;
  currentProjectId?: string;
  setCurrentProjectId?: (id: string) => void;
  activeWorkspace?: 'professional' | 'private';
  viewMode?: string;
  setViewMode?: (mode: any) => void;
  kanbanMode?: string;
  isDarkMode?: boolean;
  setIsDarkMode?: React.Dispatch<React.SetStateAction<boolean>>;
  user?: any;
  currentUserId?: string;
  tasks?: Task[];
  allTasksGlobal?: Task[];
  allAssignees?: string[];
  projectName?: string;
  projectMetadata?: ProjectMetadata;
  setProjectMetadata?: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  globalMetadata?: any;
  activeWorkshop?: any;
  workshopContext?: string;
  isSidebarCollapsed?: boolean;
  setIsSidebarCollapsed?: (collapsed: boolean) => void;
  confirmDuplicateProject?: (id: string, ref: 'milestone' | 'start' | 'end', date: string, milestoneId: string | number | null, newName?: string) => void;
  deleteProject?: (id: string) => void | Promise<void>;
  renameProject?: (id: string, name: string, color: string, visibility?: 'public' | 'private') => void;
  shareProjectWithUser?: (projectId: string, email: string, role: 'viewer' | 'editor') => Promise<void>;
  unshareProjectWithUser?: (projectId: string, email: string) => Promise<void>;
  updateProjectShareRole?: (projectId: string, email: string, role: 'viewer' | 'editor') => Promise<void>;
  archiveProject?: (id: string) => void;
  unarchiveProject?: (id: string) => void;
  updateProjectCategory?: (projectId: string, categoryId?: string) => void;
  updateProjectMetadata?: (projectId: string, updates: Partial<ProjectMetadata>) => void;
  reorderProjects?: (newOrder: FullProject[]) => void;
  handleAddAITasks?: (projectId: string, tasks: any[], config?: any, desc?: string, ans?: any, meta?: any, doc?: any) => void;
  restoreProjectVersion?: (projectId: string, versionData: any) => Promise<void>;
  commitTasks?: (newTasks: Task[], options?: any) => void;
  handleSaveTask?: (task: Task) => void;
  deleteTask?: (taskId: string | number) => void;
  duplicateTask?: (taskId: string | number, targetProjectId?: string, overrides?: Partial<Task>) => any;
  handleBrainstorm?: (name: string) => void;
  updateTask?: (taskId: string | number, updates: Partial<Task>) => void;
  updateSampleProjectForBranch?: (branchId: string) => Promise<void>;
}

export const GlobalModalsContainer: React.FC<GlobalModalsContainerProps> = React.memo((props) => {
  const projectCtx = useProject();
  const uiCtx = useUI();
  const authCtx = useAuth();
  const mr = props.modalRegistry || {};

  const {
    showBiographyModal = mr.showBiographyModal,
    setShowBiographyModal = mr.setShowBiographyModal,
    isTimeMachineOpen,
    setIsTimeMachineOpen,
    showCreditsModal = mr.showCreditsModal,
    setShowCreditsModal = mr.setShowCreditsModal,
    showAllowedUsersModal = mr.showAllowedUsersModal,
    setShowAllowedUsersModal = mr.setShowAllowedUsersModal,
    showPrivacyModal = mr.showPrivacyModal,
    setShowPrivacyModal = mr.setShowPrivacyModal,
    showAccountDeletionModal = mr.showAccountDeletionModal,
    setShowAccountDeletionModal = mr.setShowAccountDeletionModal,
    showExportProModal = mr.showExportProModal,
    setShowExportProModal = mr.setShowExportProModal,
    showCommandPalette = mr.showCommandPalette,
    setShowCommandPalette = mr.setShowCommandPalette,
    showShortcutsHelpModal = mr.showShortcutsHelpModal,
    setShowShortcutsHelpModal = mr.setShowShortcutsHelpModal,
    showUserProfileModal = mr.showUserProfileModal,
    setShowUserProfileModal = mr.setShowUserProfileModal,
    showCreditsAndHelpModal = mr.showCreditsAndHelpModal,
    setShowCreditsAndHelpModal = mr.setShowCreditsAndHelpModal,
    showQuickAddTaskModal = mr.showQuickAddTaskModal,
    setShowQuickAddTaskModal = mr.setShowQuickAddTaskModal,
    showOnboardingWizard = mr.showOnboardingWizard,
    setShowOnboardingWizard = mr.setShowOnboardingWizard,
    showGuidedTour = mr.showGuidedTour,
    setShowGuidedTour = mr.setShowGuidedTour,
    showDragDropHint = mr.showDragDropHint,
    setShowDragDropHint = mr.setShowDragDropHint,
    isProjectEditor,
    categories,
    isRightSidebarOpen,
    setIsRightSidebarOpen,
    handleAddTask,

    editingTask = uiCtx.editingTask,
    setEditingTask = uiCtx.setEditingTask,
    projectToDuplicate = uiCtx.projectToDuplicate,
    setProjectToDuplicate = uiCtx.setProjectToDuplicate,
    projectToDelete = uiCtx.projectToDelete,
    setProjectToDelete = uiCtx.setProjectToDelete,
    projectToRename = uiCtx.projectToRename,
    setProjectToRename = uiCtx.setProjectToRename,
    projectToShare = uiCtx.projectToShare,
    setProjectToShare = uiCtx.setProjectToShare,
    showProjectOverviewModal = uiCtx.showProjectOverviewModal,
    setShowProjectOverviewModal = uiCtx.setShowProjectOverviewModal,
    showAIPlanner = uiCtx.showAIPlanner,
    setShowAIPlanner = uiCtx.setShowAIPlanner,
    showLegend = uiCtx.showLegend,
    setShowLegend = uiCtx.setShowLegend,
    showChatPanel = uiCtx.showChatPanel,
    setShowChatPanel = uiCtx.setShowChatPanel,
    viewMode = uiCtx.viewMode,
    setViewMode = uiCtx.setViewMode,
    kanbanMode = uiCtx.kanbanMode,
    isDarkMode = uiCtx.isDarkMode,
    setIsDarkMode = uiCtx.setIsDarkMode,
    allAssignees = uiCtx.allAssignees,
    activeWorkshop = uiCtx.activeWorkshop,
    workshopContext = uiCtx.workshopContext,
    isSidebarCollapsed = uiCtx.isSidebarCollapsed,
    setIsSidebarCollapsed = uiCtx.setIsSidebarCollapsed,
    handleBrainstorm = uiCtx.handleBrainstorm,

    projects = projectCtx.projects,
    allProjects = projectCtx.allProjects,
    activeProject = projectCtx.activeProject,
    currentProjectId = projectCtx.currentProjectId,
    setCurrentProjectId = projectCtx.setCurrentProjectId,
    activeWorkspace = projectCtx.activeWorkspace,
    currentUserId = projectCtx.userId,
    tasks = projectCtx.tasks,
    allTasksGlobal = projectCtx.allTasksGlobal,
    projectName = projectCtx.projectName,
    projectMetadata = projectCtx.projectMetadata,
    setProjectMetadata = projectCtx.setProjectMetadata,
    globalMetadata = projectCtx.globalMetadata,
    confirmDuplicateProject = projectCtx.confirmDuplicateProject,
    deleteProject = projectCtx.deleteProject,
    renameProject = projectCtx.renameProject,
    shareProjectWithUser = projectCtx.shareProjectWithUser,
    unshareProjectWithUser = projectCtx.unshareProjectWithUser,
    updateProjectShareRole = projectCtx.updateProjectShareRole,
    archiveProject = projectCtx.archiveProject,
    unarchiveProject = projectCtx.unarchiveProject,
    updateProjectCategory = projectCtx.updateProjectCategory,
    updateProjectMetadata = projectCtx.updateProjectMetadata,
    reorderProjects = projectCtx.reorderProjects,
    handleAddAITasks = projectCtx.handleAddAITasks,
    restoreProjectVersion = projectCtx.restoreProjectVersion,
    commitTasks = projectCtx.commitTasks,
    handleSaveTask = projectCtx.handleSaveTask,
    deleteTask = projectCtx.deleteTask,
    duplicateTask = projectCtx.duplicateTask,
    updateTask = projectCtx.updateTask,
    updateSampleProjectForBranch = projectCtx.updateSampleProjectForBranch,

    user = authCtx.user,
  } = props;

  return (
    <ErrorBoundary
      fallbackTitle="Fehler im Dialogfenster"
      onReset={() => setEditingTask(null)}
    >
      <Suspense fallback={null}>
      <ProjectManagementModalsGroup
        showProjectOverviewModal={!!showProjectOverviewModal}
        setShowProjectOverviewModal={setShowProjectOverviewModal}
        allProjects={allProjects}
        projects={projects}
        activeWorkspace={activeWorkspace}
        currentProjectId={currentProjectId}
        setCurrentProjectId={setCurrentProjectId}
        viewMode={viewMode}
        setViewMode={setViewMode}
        updateProjectCategory={updateProjectCategory}
        globalMetadata={globalMetadata}
        updateProjectMetadata={updateProjectMetadata}
        reorderProjects={reorderProjects}
        projectToDuplicate={projectToDuplicate}
        setProjectToDuplicate={setProjectToDuplicate}
        confirmDuplicateProject={confirmDuplicateProject}
        projectToDelete={projectToDelete}
        setProjectToDelete={setProjectToDelete}
        deleteProject={deleteProject}
        projectToRename={projectToRename}
        setProjectToRename={setProjectToRename}
        renameProject={renameProject}
        categories={categories}
        shareProjectWithUser={shareProjectWithUser}
        unshareProjectWithUser={unshareProjectWithUser}
        updateProjectShareRole={updateProjectShareRole}
        archiveProject={archiveProject}
        unarchiveProject={unarchiveProject}
        currentUserId={currentUserId || ''}
        projectToShare={projectToShare}
        setProjectToShare={setProjectToShare}
        userEmail={user?.email}
      />

      {editingTask && (() => {
        const isEditingTaskEditable =
          isProjectEditor && canUserEditTask(editingTask, allProjects, currentUserId, user?.email);
        return (
          <TaskEditCard
            task={editingTask}
            projects={projects}
            isReadOnly={!isEditingTaskEditable}
            allTasks={allTasksGlobal}
            onClose={() => setEditingTask(null)}
            onSave={(id, updates) => {
              if (!editingTask || !isEditingTaskEditable) return;
              const targetId = id ?? editingTask.id;
              const isHeartbeatOnly = Object.keys(updates).length === 1 && 'activeEditor' in updates;
              if (isHeartbeatOnly) {
                updateTask(targetId, updates);
                return;
              }
              const isCurrentEditingTask = String(targetId) === String(editingTask.id);
              const baseForMerge = isCurrentEditingTask
                ? editingTask
                : allTasksGlobal.find((t) => String(t.id) === String(targetId)) || editingTask;
              const merged = { ...baseForMerge, ...updates, id: targetId } as Task;
              handleSaveTask(merged);
              if (isCurrentEditingTask) {
                setEditingTask(merged);
              }
            }}
            onDelete={(taskId) => {
              if (!isEditingTaskEditable) return;
              deleteTask(taskId);
            }}
            onDuplicate={duplicateTask}
            allAssignees={allAssignees}
            onBrainstorm={(task) => handleBrainstorm(task.name)}
            onAddTask={handleAddTask}
            onSelectTask={setEditingTask}
            onUpgradeChecklistItem={(itemId, text) => {
              if (!editingTask || !isEditingTaskEditable) return;
              const newTask = handleAddTask('task', editingTask.id, editingTask.projectId, text);
              if (newTask) {
                const updatedChecklist = (editingTask.checklist || []).filter((i) => i.id !== itemId);
                updateTask(editingTask.id, { checklist: updatedChecklist });
                setEditingTask({ ...editingTask, checklist: updatedChecklist });
              }
            }}
          />
        );
      })()}

      {showAIPlanner && (
        <AIProjectPlanner
          onClose={() => setShowAIPlanner(false)}
          onAddTasks={(aiTasks, config, desc, ans, meta, doc) =>
            handleAddAITasks(currentProjectId, aiTasks, config, desc, ans, meta, doc)
          }
        />
      )}

      {showLegend && <GanttLegend onClose={() => setShowLegend(false)} />}

      <AnimatePresence>
        {showBiographyModal && activeProject && (
          <ProjectBiographyModal
            project={activeProject}
            onClose={() => setShowBiographyModal(false)}
            onUpdateMetadata={(updates) => updateProjectMetadata(activeProject.id, updates)}
          />
        )}
      </AnimatePresence>

      {isTimeMachineOpen && activeProject && (
        <TimeMachineModal
          isOpen={isTimeMachineOpen}
          onClose={() => setIsTimeMachineOpen(false)}
          projectId={activeProject.id}
          projectName={activeProject.name}
          restoreProjectVersion={restoreProjectVersion}
          isReadOnly={!isProjectEditor}
        />
      )}

      {showExportProModal && (
        <ExportProModal isOpen={showExportProModal} onClose={() => setShowExportProModal(false)} />
      )}

      {showChatPanel && (
        <ProjectChatPanel
          isOpen={showChatPanel}
          onClose={() => setShowChatPanel(false)}
          projectName={projectName}
          tasks={tasks}
          onUpdateTasks={commitTasks}
          projectMetadata={projectMetadata}
          onUpdateMetadata={setProjectMetadata}
          initialWorkshop={activeWorkshop}
          initialContext={workshopContext}
          isReadOnly={!isProjectEditor}
        />
      )}

      <SystemAndOnboardingModalsGroup
        showPrivacyModal={showPrivacyModal}
        setShowPrivacyModal={setShowPrivacyModal}
        showAccountDeletionModal={showAccountDeletionModal}
        setShowAccountDeletionModal={setShowAccountDeletionModal}
        showCreditsModal={showCreditsModal}
        setShowCreditsModal={setShowCreditsModal}
        showAllowedUsersModal={showAllowedUsersModal}
        setShowAllowedUsersModal={setShowAllowedUsersModal}
        showCommandPalette={showCommandPalette}
        setShowCommandPalette={setShowCommandPalette}
        showShortcutsHelpModal={showShortcutsHelpModal}
        setShowShortcutsHelpModal={setShowShortcutsHelpModal}
        showUserProfileModal={showUserProfileModal}
        setShowUserProfileModal={setShowUserProfileModal}
        showCreditsAndHelpModal={showCreditsAndHelpModal}
        setShowCreditsAndHelpModal={setShowCreditsAndHelpModal}
        showQuickAddTaskModal={showQuickAddTaskModal}
        setShowQuickAddTaskModal={setShowQuickAddTaskModal}
        showOnboardingWizard={showOnboardingWizard}
        setShowOnboardingWizard={setShowOnboardingWizard}
        showGuidedTour={showGuidedTour}
        setShowGuidedTour={setShowGuidedTour}
        showDragDropHint={showDragDropHint}
        setShowDragDropHint={setShowDragDropHint}
        setShowProjectOverviewModal={setShowProjectOverviewModal}
        setShowExportProModal={setShowExportProModal}
        setShowAIPlanner={setShowAIPlanner}
        setCurrentProjectId={setCurrentProjectId}
        setEditingTask={setEditingTask}
        setIsDarkMode={setIsDarkMode}
        isDarkMode={isDarkMode}
        viewMode={viewMode}
        setViewMode={setViewMode}
        kanbanMode={kanbanMode}
        isRightSidebarOpen={isRightSidebarOpen}
        setIsRightSidebarOpen={setIsRightSidebarOpen}
        isSidebarCollapsed={isSidebarCollapsed}
        setIsSidebarCollapsed={setIsSidebarCollapsed}
        allProjects={allProjects}
        updateSampleProjectForBranch={updateSampleProjectForBranch}
        userEmail={user?.email}
        currentUserId={currentUserId}
      />
      </Suspense>
    </ErrorBoundary>
  );
});

GlobalModalsContainer.displayName = 'GlobalModalsContainer';

export default GlobalModalsContainer;
